import { useEffect, useState, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import axiosInstance from "../../../../services/AxiosInstance"
import Select from "react-select"
import { Button, Spinner } from "react-bootstrap"
import defaultAvatar from "../../../../../src/assets/images/placeholder.jpg"
import { fetchAnswers, saveAnswers } from "../../../../store/actions/QuestionnaireActions"
import { getPatientDetailsAction } from "../../../../store/actions/PatientsAction"
import { getImportantQuestionsAction } from "../../../../store/actions/ImportantQuestionAction"
import "react-toastify/dist/ReactToastify.css"
import { showErrorAlert } from "../../../../services/ErrorHandlingService"
import { formatName } from "../../../../utils/formatName.js"
import { FaMicrophone } from "react-icons/fa"
import { useRef } from "react"
import { getInternalType } from "../../../../utils/questionTypeUtils"

const Questionnaire = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [notes, setNotes] = useState({})
  const [checkedQuestions, setCheckedQuestions] = useState({})
  const [isInitialized, setIsInitialized] = useState(false)
  const [negativeQuestions, setNegativeQuestions] = useState({})
  const [diseaseStatus, setDiseaseStatus] = useState({})
  const [saving, setSaving] = useState(false)
  const [formDetails, setFormDetails] = useState(null)
  const [formQuestions, setFormQuestions] = useState({})
  const [detectedFormId, setDetectedFormId] = useState(null)
  const [editableAnswers, setEditableAnswers] = useState({})
  const location = useLocation()

  const {
    data: answers,
    loading,
    error,
  } = useSelector((state) => {
    console.log("State in Questionnaire:", state)
    return state.questionnaire.answers
  })

  const importantQuestions = useSelector((state) => state.importantQuestions?.importantQuestions || [])
  const patientDetails = useSelector((state) => state.patients?.patientDetails)

  const recognitionRef = useRef(null)
  const [isRecordingKey, setIsRecordingKey] = useState(null)
  const [speechLang, setSpeechLang] = useState("en-IN")

  const toggleSpeechLang = () => {
    setSpeechLang((prev) => (prev === "en-IN" ? "mr-IN" : "en-IN"))
  }

  const startListeningForNote = (question) => {
    const key = getQuestionKey(question)
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition

    if (!SpeechRecognition) {
      alert("Speech recognition not supported")
      return
    }

    if (recognitionRef.current) return

    const recognition = new SpeechRecognition()
    recognitionRef.current = recognition
    recognition.lang = speechLang
    recognition.continuous = false
    recognition.interimResults = false
    setIsRecordingKey(key)

    recognition.onresult = (event) => {
      const spokenText = event.results[0][0].transcript
      setNotes((prev) => ({
        ...prev,
        [key]: prev[key] ? prev[key] + " " + spokenText : spokenText,
      }))
    }

    recognition.onend = () => {
      recognitionRef.current = null
      setIsRecordingKey(null)
    }

    recognition.onerror = () => {
      recognitionRef.current = null
      setIsRecordingKey(null)
    }

    recognition.start()
  }

  const getQuestionKey = (question) => {
    return `${question.group_id || "standalone"}-${question.question_answer_id || question.question_id}`
  }

  const handleNegativeChange = (question, checked) => {
    const key = getQuestionKey(question)
    setNegativeQuestions((prev) => ({
      ...prev,
      [key]: checked,
    }))

    if (!checked) {
      setDiseaseStatus((prev) => ({
        ...prev,
        [key]: "",
      }))
    }
  }

  const handleDiseaseStatusChange = (questionKey, value) => {
    setDiseaseStatus((prev) => ({
      ...prev,
      [questionKey]: value,
    }))
  }

  const getValidPhotoUrl = (photoUrl) => {
    if (!photoUrl) return defaultAvatar

    if ((photoUrl.match(/https:\/\//g) || []).length > 1) {
      const parts = photoUrl.split("https://")
      return "https://" + parts[parts.length - 1]
    }

    return photoUrl
  }

  const handleAnswerChange = (question, value) => {
    const key = getQuestionKey(question)
    setEditableAnswers((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const fetchFormDetails = async (formId) => {
    if (!formId || formId === "0" || formId === detectedFormId) return

    try {
      console.log("Fetching form details for form_id:", formId)

      const response = await axiosInstance.post("/admin/formDetails", {
        form_id: formId,
      })

      if (response.data.status === "success") {
        const formData = response.data.data
        setFormDetails(formData)

        const questionsMap = {}
        ;(formData.questions || []).forEach((item) => {
          if (item.isGroup === "No") {
            questionsMap[item.question_id] = {
              type: item.question_type || "short_answer",
              label: item.question || item.label || "",
              options: item.options || [],
              description: item.description || "",
              is_required: item.is_required === "Yes",
            }
          }

          if (item.isGroup === "Yes" && item.questions) {
            item.questions.forEach((q) => {
              questionsMap[q.question_id] = {
                type: q.question_type || "short_answer",
                label: q.question || q.label || "",
                options: q.options || [],
                description: q.description || "",
                is_required: q.is_required === "Yes",
              }
            })
          }
        })

        setFormQuestions(questionsMap)
        setDetectedFormId(formId)

        console.log("Form questions loaded:", questionsMap)
      }
    } catch (error) {
      console.error("Error fetching form details:", error)
    }
  }

  useEffect(() => {
    const statePatient = location.state?.patientData

    if (statePatient) {
      localStorage.setItem("selectedPatient", JSON.stringify(statePatient))
      dispatch({ type: "CONFIRMED_GET_PATIENT_DETAILS", payload: statePatient })

      if (statePatient.patient_id) {
        dispatch(fetchAnswers({ patient_id: statePatient.patient_id, is_important: "No" }))
        dispatch(getImportantQuestionsAction(statePatient.patient_id, "Yes"))
      }

      dispatch(getPatientDetailsAction(statePatient.patient_id))
    }
  }, [location.state, dispatch])

  useEffect(() => {
    if (patientDetails) {
      localStorage.setItem("selectedPatient", JSON.stringify(patientDetails))
    }
  }, [patientDetails])

  useEffect(() => {
    if (answers && answers.length > 0 && !loading && !detectedFormId) {
      console.log("Answers received:", answers)

      let foundFormId = null

      const standaloneQuestion = answers.find((item) => item.isGroup === "No")
      if (standaloneQuestion?.form_id && standaloneQuestion.form_id !== "0") {
        foundFormId = standaloneQuestion.form_id
        console.log("Found form_id from standalone question:", foundFormId)
      }

      if (!foundFormId) {
        const groupedQuestion = answers.find((item) => item.isGroup === "Yes")
        if (groupedQuestion?.form_id && groupedQuestion.form_id !== "0") {
          foundFormId = groupedQuestion.form_id
          console.log("Found form_id from grouped question:", foundFormId)
        } else if (groupedQuestion?.questions?.[0]?.form_id && groupedQuestion.questions[0].form_id !== "0") {
          foundFormId = groupedQuestion.questions[0].form_id
          console.log("Found form_id from grouped question item:", foundFormId)
        }
      }

      if (!foundFormId && importantQuestions.length > 0) {
        const importantQuestion = importantQuestions[0]
        if (importantQuestion?.form_id && importantQuestion.form_id !== "0") {
          foundFormId = importantQuestion.form_id
          console.log("Found form_id from important question:", foundFormId)
        }
      }

      if (foundFormId) {
        fetchFormDetails(foundFormId)
      } else if (patientDetails?.form_id && patientDetails.form_id !== "0") {
        console.log("Using fallback form_id from patient details:", patientDetails.form_id)
        fetchFormDetails(patientDetails.form_id)
      }
    }
  }, [answers, importantQuestions, loading, detectedFormId, patientDetails])

  useEffect(() => {
    if (patientDetails?.patient_id) {
      dispatch(
        fetchAnswers({
          patient_id: patientDetails.patient_id,
          is_important: "No",
        }),
      )

      dispatch(getImportantQuestionsAction(patientDetails.patient_id, "Yes"))
    }
  }, [dispatch, patientDetails?.patient_id])

  useEffect(() => {
    if ((answers || importantQuestions.length > 0) && !isInitialized && !loading) {
      const initialCheckedState = {}
      const initialNegativeState = {}
      const initialDiseaseStatus = {}
      const initialNotesState = {}
      const initialEditableAnswers = {}

      try {
        if (answers) {
          const standaloneQuestions = answers.filter((item) => item.isGroup === "No")
          standaloneQuestions.forEach((answer) => {
            const key = getQuestionKey(answer)
            initialCheckedState[key] = answer.is_important === "Yes"
            initialNegativeState[key] = answer.is_negative === "Yes"
            initialNotesState[key] = answer.note_description || ""
            initialEditableAnswers[key] = answer.answer || ""
            if (answer.is_negative === "Yes" && answer.disease_status) {
              initialDiseaseStatus[key] = answer.disease_status
            }
          })

          const groupedQuestions = answers.filter((item) => item.isGroup === "Yes")
          groupedQuestions.forEach((group) => {
            if (group.questions) {
              group.questions.forEach((question) => {
                const key = getQuestionKey(question)
                initialCheckedState[key] = question.is_important === "Yes"
                initialNegativeState[key] = question.is_negative === "Yes"
                initialNotesState[key] = question.note_description || ""
                initialEditableAnswers[key] = question.answer || ""
                if (question.is_negative === "Yes" && question.disease_status) {
                  initialDiseaseStatus[key] = question.disease_status
                }
              })
            }
          })
        }

        importantQuestions.forEach((question) => {
          const key = getQuestionKey(question)
          initialCheckedState[key] = question.is_important === "Yes"
          initialNegativeState[key] = question.is_negative === "Yes"
          initialNotesState[key] = question.note_description || ""
          initialEditableAnswers[key] = question.answer || ""
          if (question.is_negative === "Yes" && question.disease_status) {
            initialDiseaseStatus[key] = question.disease_status
          }
        })

        setCheckedQuestions(initialCheckedState)
        setNegativeQuestions(initialNegativeState)
        setDiseaseStatus(initialDiseaseStatus)
        setNotes(initialNotesState)
        setEditableAnswers(initialEditableAnswers)
        setIsInitialized(true)

        console.log("Initialized with notes:", initialNotesState)
      } catch (error) {
        console.error("Error initializing questions:", error)
      }
    }
  }, [answers, importantQuestions, loading, isInitialized])

  useEffect(() => {
    setIsInitialized(false)
    setCheckedQuestions({})
    setNegativeQuestions({})
    setDiseaseStatus({})
    setNotes({})
    setEditableAnswers({})
    setFormQuestions({})
    setFormDetails(null)
    setDetectedFormId(null)
  }, [patientDetails?.patient_id])

  const handleNoteChange = (question, value) => {
    const key = getQuestionKey(question)
    setNotes((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const handleCheckboxChange = (question) => {
    const key = getQuestionKey(question)
    setCheckedQuestions((prev) => {
      const current = !!prev[key]
      return {
        ...prev,
        [key]: !current,
      }
    })
  }

  // Combine Important and Negative questions into one array
  const combinedImportantAndNegativeQuestions = useMemo(() => {
    const combined = []
    const questionMap = new Map()

    // Add important questions
    importantQuestions.forEach((question) => {
      const id = question.question_answer_id || question.question_id
      if (!questionMap.has(id)) {
        questionMap.set(id, question)
        combined.push(question)
      }
    })

    // Add negative questions from answers
    if (answers && answers.length > 0) {
      // Standalone questions with is_negative = "Yes"
      const standaloneNegative = answers.filter(
        (item) => item.isGroup === "No" && item.is_negative === "Yes"
      )
      standaloneNegative.forEach((question) => {
        const id = question.question_answer_id || question.question_id
        if (!questionMap.has(id)) {
          questionMap.set(id, question)
          combined.push(question)
        }
      })

      // Grouped questions with is_negative = "Yes"
      const groupedQuestions = answers.filter((item) => item.isGroup === "Yes")
      groupedQuestions.forEach((group) => {
        if (group.questions && group.questions.length > 0) {
          const negativeInGroup = group.questions.filter(
            (question) => question.is_negative === "Yes"
          )
          negativeInGroup.forEach((question) => {
            const id = question.question_answer_id || question.question_id
            if (!questionMap.has(id)) {
              questionMap.set(id, question)
              combined.push(question)
            }
          })
        }
      })
    }

    return combined
  }, [importantQuestions, answers])

  const transformPayloadForBackend = (answers, importantQuestions) => {
    const transformedPayload = []

    const standaloneQuestions = (answers || [])
      .filter((item) => item.isGroup === "No")
      .map((answer, index) => {
        const questionId = answer.question_id?.toString()
        const formQuestionData = formQuestions[questionId] || {}

        return {
          id: questionId,
          label: answer.question || answer.label || "",
          type: formQuestionData.type || "short_answer",
          isGroup: "No",
          order: answer.order || index + 1,
          isRequired: answer.is_required === "Yes",
          isImportant: checkedQuestions[getQuestionKey(answer)] ? true : false,
          options: answer.options || [],
        }
      })

    const groupedQuestions = (answers || [])
      .filter((item) => item.isGroup === "Yes")
      .map((group, groupIndex) => {
        const groupQuestions = group.questions.map((question, questionIndex) => {
          const questionId = question.question_id?.toString()
          const formQuestionData = formQuestions[questionId] || {}

          const transformedOptions = (question.options || []).map((option) => {
            const optionObj = {
              id: option.id || option.option_id?.toString(),
              label: option.label || option.option_name,
            }

            if (option.option_questions && option.option_questions.length > 0) {
              optionObj.option_questions = option.option_questions.map((optQuestion) => ({
                id: optQuestion.id || optQuestion.question_id?.toString(),
                label: optQuestion.label || optQuestion.question,
                type: formQuestions[optQuestion.question_id?.toString()]?.type || "short_answer",
                order: optQuestion.order || 1,
                isRequired: optQuestion.is_required === "Yes",
                isImportant: optQuestion.is_important === "Yes",
                is_primary: optQuestion.is_primary || "Yes",
              }))
            }

            return optionObj
          })

          return {
            id: questionId,
            label: question.question || question.label || "",
            type: formQuestionData.type || "short_answer",
            order: question.order || questionIndex + 1,
            isRequired: question.is_required === "Yes",
            isImportant: checkedQuestions[getQuestionKey(question)] ? true : false,
            is_primary: question.is_primary || "No",
            options: transformedOptions,
          }
        })

        return {
          id: group.group_id || `g${groupIndex + 1}`,
          label: group.group_name || `Group ${groupIndex + 1}`,
          isGroup: "Yes",
          isTab: false,
          order: group.order || groupIndex + 1,
          questions: groupQuestions,
        }
      })

    const transformedImportantQuestions = importantQuestions.map((question, index) => {
      const questionId = question.question_id?.toString()
      const formQuestionData = formQuestions[questionId] || {}

      return {
        id: questionId,
        label: question.question || question.label || "",
        type: formQuestionData.type || "short_answer",
        isGroup: "No",
        order: question.order || index + 1,
        isRequired: question.is_required === "Yes",
        isImportant: checkedQuestions[getQuestionKey(question)] ? true : false,
        options: question.options || [],
      }
    })

    transformedPayload.push(...standaloneQuestions)
    transformedPayload.push(...groupedQuestions)
    transformedPayload.push(...transformedImportantQuestions)

    return transformedPayload
  }

  const logTransformedPayload = (payload) => {
    console.log("=== TRANSFORMED PAYLOAD FOR BACKEND ===")
    console.log(JSON.stringify(payload, null, 2))
    console.log("=== END TRANSFORMED PAYLOAD ===")
  }

  const handleSave = async () => {
    if (saving) return
    setSaving(true)
  
    if (!answers && !importantQuestions.length) {
      setSaving(false)
      return
    }
  
    try {
      const transformedPayload = transformPayloadForBackend(answers, importantQuestions)
      logTransformedPayload(transformedPayload)
  
      const standaloneAnswers = (answers || [])
        .filter((item) => item.isGroup === "No")
        .map((answer) => {
          const key = getQuestionKey(answer)
          return {
            question_answer_id: answer.question_answer_id?.toString() || answer.question_id?.toString() || "0",
            note_description: notes[key] || answer.note_description || "",
            is_important: checkedQuestions[key] ? "Yes" : "No",
            is_negative: negativeQuestions[key] ? "Yes" : "No",
            disease_status: negativeQuestions[key] ? diseaseStatus[key] || "" : "",
            form_id: answer.form_id || patientDetails?.form_id || "0",
            answer: editableAnswers[key] || answer.answer || "",
          }
        })
  
      const groupedAnswers = (answers || [])
        .filter((item) => item.isGroup === "Yes")
        .flatMap((group) =>
          group.questions.map((question) => {
            const key = getQuestionKey(question)
            return {
              question_answer_id: question.question_answer_id?.toString() || question.question_id?.toString() || "0",
              note_description: notes[key] || question.note_description || "",
              is_important: checkedQuestions[key] ? "Yes" : "No",
              is_negative: negativeQuestions[key] ? "Yes" : "No",
              disease_status: negativeQuestions[key] ? diseaseStatus[key] || "" : "",
              form_id: question.form_id || group.form_id || patientDetails?.form_id || "0",
              answer: editableAnswers[key] || question.answer || "",
            }
          }),
        )
  
      const importantAnswers = importantQuestions.map((question) => {
        const key = getQuestionKey(question)
        return {
          question_answer_id: question.question_answer_id?.toString() || question.question_id?.toString() || "0",
          note_description: notes[key] || question.note_description || "",
          is_important: checkedQuestions[key] ? "Yes" : "No",
          is_negative: negativeQuestions[key] ? "Yes" : "No",
          disease_status: negativeQuestions[key] ? diseaseStatus[key] || "" : "",
          form_id: question.form_id || patientDetails?.form_id || "0",
        }
      })
  
      const finalAnswersPayload = [
        ...standaloneAnswers,
        ...groupedAnswers,
        ...importantAnswers,
      ]
  
      const form_id =
        standaloneAnswers[0]?.form_id ||
        groupedAnswers[0]?.form_id ||
        importantAnswers[0]?.form_id ||
        patientDetails?.form_id ||
        "0"
  
      await dispatch(
        saveAnswers(
          {
            patient_id: patientDetails.patient_id,
            form_id,
            answers: JSON.stringify(finalAnswersPayload),
          },
          navigate,
        ),
      )
  
      setIsInitialized(false)
      dispatch(fetchAnswers({ patient_id: patientDetails.patient_id, is_important: "No" }))
      dispatch(getImportantQuestionsAction(patientDetails.patient_id, "Yes"))
    } catch (error) {
      console.error("Error saving answers:", error)
      showErrorAlert("Failed to save answers. Please try again.")
    } finally {
      setSaving(false)
    }
  }
  
  // Helper function to check if value is base64 data URL
  const isBase64Data = (str) => {
    return str && (str.startsWith('data:application/pdf;base64,') || 
                   str.startsWith('data:image/') ||
                   str.startsWith('data:application/'));
  }

  // Helper function to get clean filename from base64 or URL
  const getCleanFileName = (url = "") => {
    if (!url) return "";
    if (url.startsWith('http')) {
      const file = url.split("/").pop();
      return file.replace(/^\d+_/, "").substring(0, 50);
    }
    if (url.startsWith('data:application/pdf;base64,')) return 'Document.pdf';
    if (url.startsWith('data:image/png')) return 'Image.png';
    if (url.startsWith('data:image/jpeg')) return 'Image.jpg';
    if (url.startsWith('data:image/jpg')) return 'Image.jpg';
    if (url.startsWith('data:application/')) return 'File.pdf';
    return 'File';
  }

  // Helper function to get file icon
  const getFileIcon = (url) => {
    if (url && (url.includes('.pdf') || url.includes('application/pdf'))) {
      return <i className="fa fa-file-pdf-o me-2" style={{ color: '#dc3545' }}></i>;
    }
    if (url && (url.includes('.jpg') || url.includes('.jpeg') || url.includes('.png') || url.includes('.gif'))) {
      return <i className="fa fa-file-image-o me-2" style={{ color: '#28a745' }}></i>;
    }
    return <i className="fa fa-file-o me-2"></i>;
  }

  const renderAnswerField = (item) => {
    const key = getQuestionKey(item)
    const value = editableAnswers[key] || item.answer || ""

    const questionId = item.question_id?.toString()
    const formQuestionData = formQuestions[questionId] || {}
    const questionType = formQuestionData.type || item.question_type
    const options = formQuestionData.options || item.options || []

    const internalType = getInternalType(questionType)

    const renderSelectedOptionQuestions = (selectedOption) => {
      if (!selectedOption || !selectedOption.option_questions || selectedOption.option_questions.length === 0) {
        return null
      }

      return (
        <div className="ms-4 mt-2 p-3 bg-white rounded border">
          <div className="small text-muted mb-2">Sub-questions:</div>
          {selectedOption.option_questions.map((optionQuestion, idx) => (
            <div className="ms-4 mt-2 mb-2 p-2 bg-white rounded border" key={optionQuestion.question_id || idx}>
              <div className="mb-1">
                <span className="fw-semibold">{optionQuestion.label || optionQuestion.question}: </span>
              </div>
              <span className="text-muted">{optionQuestion.questionType || "-"}</span>
            </div>
          ))}
        </div>
      )
    }

    // Check if this is a file input question
    const isFileInput = internalType === "file_input" || questionType === "File Input";

    if (isFileInput) {
      const hasFile = value && value.trim() !== "";
      
      if (!hasFile) {
        return (
          <div className="text-muted">
            <i className="fa fa-file-o me-2"></i>
            No file uploaded
          </div>
        );
      }

      // Check if it's a base64 data URL or regular URL
      const isBase64 = isBase64Data(value);
      const isPdf = isBase64 ? value.includes('application/pdf') : (value.toLowerCase().endsWith('.pdf') || value.includes('application/pdf'));
      const isImage = isBase64 ? value.includes('image/') : (value.toLowerCase().endsWith('.png') || value.toLowerCase().endsWith('.jpg') || value.toLowerCase().endsWith('.jpeg'));
      const fileName = getCleanFileName(value);
      
      // For base64 PDF, create a blob URL for better handling
      let displayUrl = value;
      if (isBase64 && isPdf) {
        try {
          // Extract base64 data and create blob URL
          const base64Data = value.split(',')[1];
          if (base64Data) {
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            displayUrl = URL.createObjectURL(blob);
          }
        } catch (e) {
          console.error("Error creating blob URL:", e);
          displayUrl = value;
        }
      }

      return (
        <div>
          <a 
            href={displayUrl} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="btn btn-outline-primary btn-sm"
            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
          >
            {getFileIcon(value)}
            <span>{fileName}</span>
            <i className="fa fa-external-link ms-2"></i>
          </a>
          {isBase64 && (
            <div className="text-muted small mt-1">
              <i className="fa fa-info-circle me-1"></i>
              PDF document (click to view)
            </div>
          )}
        </div>
      );
    }

    switch (internalType) {
      case "short_answer":
        return (
          <input
            type="text"
            className="form-control"
            value={value}
            onChange={(e) => handleAnswerChange(item, e.target.value)}
            placeholder="Enter your answer"
          />
        )

      case "paragraph":
        return (
          <textarea
            className="form-control"
            rows={3}
            value={value}
            onChange={(e) => handleAnswerChange(item, e.target.value)}
            placeholder="Enter detailed answer"
          />
        )

      case "multiple_choice":
        return (
          <div>
            {options.map((opt, index) => {
              const optionLabel = typeof opt === "string" ? opt : opt.label || opt.option_name
              const optionValue = typeof opt === "string" ? opt : opt.option_name || opt.label
              const isSelected = value === optionValue
              const hasOptionQuestions = opt?.option_questions && opt.option_questions.length > 0

              return (
                <div key={opt?.option_id || opt?.id || index} className="mb-2">
                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      name={`radio_${key}`}
                      checked={isSelected}
                      onChange={() => handleAnswerChange(item, optionValue)}
                    />
                    <label className="form-check-label">
                      {optionLabel}
                      {hasOptionQuestions && (
                        <span className="badge bg-info ms-2" title={`${opt.option_questions.length} sub-questions`}>
                          {opt.option_questions.length}
                        </span>
                      )}
                    </label>
                  </div>
                  {isSelected && hasOptionQuestions && renderSelectedOptionQuestions(opt)}
                </div>
              )
            })}
          </div>
        )

      case "checkbox":
      case "multiple_selection":
        let selectedValues = []

        if (Array.isArray(value)) {
          selectedValues = value
        } else if (typeof value === "string") {
          selectedValues = value
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean)
        }

        return (
          <div>
            {options.map((opt, index) => {
              const optionLabel = typeof opt === "string" ? opt : opt.label || opt.option_name
              const optionValue = typeof opt === "string" ? opt : opt.option_name || opt.label
              const isSelected = selectedValues.includes(optionValue)
              const hasOptionQuestions = opt?.option_questions && opt.option_questions.length > 0

              return (
                <div key={opt?.option_id || opt?.id || index} className="mb-2">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={isSelected}
                      onChange={(e) => {
                        const newValues = e.target.checked
                          ? [...selectedValues, optionValue]
                          : selectedValues.filter((v) => v !== optionValue)
                        handleAnswerChange(item, newValues.join(", "))
                      }}
                    />
                    <label className="form-check-label">
                      {optionLabel}
                      {hasOptionQuestions && (
                        <span className="badge bg-info ms-2" title={`${opt.option_questions.length} sub-questions`}>
                          {opt.option_questions.length}
                        </span>
                      )}
                    </label>
                  </div>
                  {isSelected && hasOptionQuestions && renderSelectedOptionQuestions(opt)}
                </div>
              )
            })}
          </div>
        )

      case "date":
        return (
          <input
            type="date"
            className="form-control"
            value={value}
            onChange={(e) => handleAnswerChange(item, e.target.value)}
          />
        )

      case "time":
        return (
          <div className="input-group">
            <input
              type="time"
              className="form-control"
              value={value}
              onChange={(e) => handleAnswerChange(item, e.target.value)}
            />
            <span className="input-group-text">
              <i className="far fa-clock"></i>
            </span>
          </div>
        )

      case "number":
      case "range":
        return (
          <input
            type="number"
            className="form-control"
            value={value}
            onChange={(e) => handleAnswerChange(item, e.target.value)}
          />
        )

      case "dropdown":
        return (
          <select className="form-control" value={value} onChange={(e) => handleAnswerChange(item, e.target.value)}>
            <option value="">Select...</option>
            {options.map((opt, index) => {
              const optionLabel = typeof opt === "string" ? opt : opt.label || opt.option_name
              const optionValue = typeof opt === "string" ? opt : opt.option_name || opt.label
              return (
                <option key={opt?.option_id || opt?.id || index} value={optionValue}>
                  {optionLabel}
                </option>
              )
            })}
          </select>
        )

      default:
        return (
          <input
            type="text"
            className="form-control"
            value={value}
            onChange={(e) => handleAnswerChange(item, e.target.value)}
            placeholder="Enter your answer"
          />
        )
    }
  }

  // Updated UI for both Important and Negative Questions - Now with editable answer field (including file input button)
  const renderQuestionWithCheckboxes = (item) => {
    const key = getQuestionKey(item)
    const isNegative = !!negativeQuestions[key];
    
    return (
      <div key={item.question_answer_id} className="col-xl-12 mb-3">
        <div
          className="timeline-panel flex-wrap border-0 p-3 rounded"
          style={{
            backgroundColor: isNegative ? "#ffe6e6" : "#f8f9fa",
            transition: "background-color 0.3s ease",
          }}
        >
          <div className="row align-items-start">
            <div className="col-md-2 d-flex flex-column align-items-start">
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`negativeCheck-${key}`}
                  checked={isNegative}
                  onChange={(e) => handleNegativeChange(item, e.target.checked)}
                />
                <label
                  className="form-check-label ms-2"
                  htmlFor={`negativeCheck-${key}`}
                  style={{
                    color: isNegative ? "#d9534f" : "inherit",
                    fontWeight: isNegative ? "600" : "400",
                  }}
                >
                  Negative
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`mainCheck-${key}`}
                  checked={!!checkedQuestions[key]}
                  onChange={() => handleCheckboxChange(item)}
                />
                <label
                  className="form-check-label ms-2"
                  htmlFor={`mainCheck-${key}`}
                  style={{
                    color: checkedQuestions[key] ? "#0d6efd" : "inherit",
                    fontWeight: checkedQuestions[key] ? "600" : "400",
                  }}
                >
                  Important
                </label>
              </div>
            </div>

            {/* UPDATED: Now using renderAnswerField for editable answer with file input button */}
            <div className="col-md-5">
              <div className="mb-2">
                <span className="fw-bold text-dark me-1">Q:</span>
                <span className="fw-semibold text-dark">
                  {item.short_form?.trim()
                    ? item.short_form
                    : item.label || item.question || ""}
                </span>
              </div>
              <div className="mb-2 d-flex align-items-center">
  <span className="fw-bold me-2">A:</span>

  <div className="flex-grow-1">
    {renderAnswerField(item)}
  </div>
</div>
            </div>

            <div className="col-md-5">
              <div className="row g-2 align-items-center">
                <div className="col-md-7">
                  <div className="position-relative">
                    <textarea
                      className="form-control pe-5"
                      placeholder="Enter Notes..."
                      value={notes[key] || item.note_description || ""}
                      onChange={(e) => handleNoteChange(item, e.target.value)}
                      style={{
                        height: "45px",
                        resize: "none",
                        paddingTop: "10px",
                      }}
                    />

                    <button
                      type="button"
                      onClick={toggleSpeechLang}
                      title="Toggle Language"
                      style={{
                        position: "absolute",
                        right: "44px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        padding: "2px 8px",
                        borderRadius: "10px",
                        border: "1px solid #ccc",
                        background: "#fff",
                        fontSize: "11px",
                        cursor: "pointer",
                      }}
                    >
                      {speechLang === "en-IN" ? "EN" : "MR"}
                    </button>

                    <button
                      type="button"
                      onClick={() => startListeningForNote(item)}
                      disabled={isRecordingKey === key}
                      style={{
                        position: "absolute",
                        right: "8px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: "30px",
                        height: "30px",
                        borderRadius: "50%",
                        border: "none",
                        background: isRecordingKey === key ? "#ff4d4d" : "#f1f3f5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: isRecordingKey === key ? "0 0 8px rgba(255,0,0,0.6)" : "0 2px 4px rgba(0,0,0,0.2)",
                        cursor: "pointer",
                      }}
                    >
                      <FaMicrophone size={14} color={isRecordingKey === key ? "white" : "#333"} />
                    </button>
                  </div>
                </div>

                {isNegative && (
                  <div className="col-md-5">
                    <Select
                      name={`disease_status_${key}`}
                      value={
                        diseaseStatus[key] || item.disease_status
                          ? { value: diseaseStatus[key] || item.disease_status, label: diseaseStatus[key] || item.disease_status }
                          : null
                      }
                      onChange={(selectedOption) =>
                        handleDiseaseStatusChange(
                          key,
                          selectedOption ? selectedOption.value : ""
                        )
                      }
                      placeholder="Status"
                      options={[
                        { value: "Active", label: "Active" },
                        { value: "Resolved", label: "Resolved" },
                        { value: "Remission", label: "Remission" },
                        { value: "Chronic", label: "Chronic" },
                        { value: "Recovered", label: "Recovered" },
                        { value: "Progressing", label: "Progressing" },
                        { value: "Stable", label: "Stable" },
                        { value: "Terminal", label: "Terminal" },
                      ]}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minHeight: "55px",
                          height: "55px",
                        }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          padding: "4px",
                        }),
                        indicatorSeparator: () => ({ display: "none" }),
                      }}
                      isClearable
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  const renderQuestion = (item) => {
    if (!item.question && !item.label) return null
    const key = getQuestionKey(item)
    const isNegative = !!negativeQuestions[key]

    return (
      <div key={item.question_answer_id} className="col-xl-12 mb-3">
        <div
          className="timeline-panel flex-wrap border-0 p-3 rounded"
          style={{
            backgroundColor: isNegative ? "#ffe6e6" : "#f8f9fa",
            transition: "background-color 0.3s ease",
          }}
        >
          <div className="row align-items-start">
            <div className="col-md-2 d-flex flex-column align-items-start">
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`negativeCheck-${key}`}
                  checked={isNegative}
                  onChange={(e) => handleNegativeChange(item, e.target.checked)}
                />
                <label
                  className="form-check-label ms-2"
                  htmlFor={`negativeCheck-${key}`}
                  style={{
                    color: isNegative ? "#d9534f" : "inherit",
                    fontWeight: isNegative ? "600" : "400",
                  }}
                >
                  Negative
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`mainCheck-${key}`}
                  checked={!!checkedQuestions[key]}
                  onChange={() => handleCheckboxChange(item)}
                />
                <label
                  className="form-check-label ms-2"
                  htmlFor={`mainCheck-${key}`}
                  style={{
                    color: checkedQuestions[key] ? "#0d6efd" : "inherit",
                    fontWeight: checkedQuestions[key] ? "600" : "400",
                  }}
                >
                  Important
                </label>
              </div>
            </div>

            <div className="col-md-5">
              <div className="mb-2">
                <span className="fw-bold text-dark me-1">Q:</span>
                <span className="fw-semibold text-dark">
                  {item.short_form?.trim()
                    ? item.short_form
                    : item.label || item.question || ""}
                </span>
              </div>
              <div className="mb-2 d-flex align-items-center">
  <span className="fw-bold me-2">A:</span>

  <div className="flex-grow-1">
    {renderAnswerField(item)}
  </div>
</div>
            </div>

            <div className="col-md-5">
              <div className="row g-2 align-items-center">
                <div className="col-md-7">
                  <div className="position-relative">
                    <textarea
                      className="form-control pe-5"
                      placeholder="Enter Notes..."
                      value={notes[key] || ""}
                      onChange={(e) => handleNoteChange(item, e.target.value)}
                      style={{
                        height: "45px",
                        resize: "none",
                        paddingTop: "10px",
                      }}
                    />

                    <button
                      type="button"
                      onClick={toggleSpeechLang}
                      title="Toggle Language"
                      style={{
                        position: "absolute",
                        right: "44px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        padding: "2px 8px",
                        borderRadius: "10px",
                        border: "1px solid #ccc",
                        background: "#fff",
                        fontSize: "11px",
                        cursor: "pointer",
                      }}
                    >
                      {speechLang === "en-IN" ? "EN" : "MR"}
                    </button>

                    <button
                      type="button"
                      onClick={() => startListeningForNote(item)}
                      disabled={isRecordingKey === key}
                      style={{
                        position: "absolute",
                        right: "8px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: "30px",
                        height: "30px",
                        borderRadius: "50%",
                        border: "none",
                        background: isRecordingKey === key ? "#ff4d4d" : "#f1f3f5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: isRecordingKey === key ? "0 0 8px rgba(255,0,0,0.6)" : "0 2px 4px rgba(0,0,0,0.2)",
                        cursor: "pointer",
                      }}
                    >
                      <FaMicrophone size={14} color={isRecordingKey === key ? "white" : "#333"} />
                    </button>
                  </div>
                </div>

                {isNegative && (
                  <div className="col-md-5">
                    <Select
                      name={`disease_status_${key}`}
                      value={diseaseStatus[key] ? { value: diseaseStatus[key], label: diseaseStatus[key] } : null}
                      onChange={(selectedOption) =>
                        handleDiseaseStatusChange(key, selectedOption ? selectedOption.value : "")
                      }
                      placeholder="Status"
                      options={[
                        { value: "Active", label: "Active" },
                        { value: "Resolved", label: "Resolved" },
                        { value: "Remission", label: "Remission" },
                        { value: "Chronic", label: "Chronic" },
                        { value: "Recovered", label: "Recovered" },
                        { value: "Progressing", label: "Progressing" },
                        { value: "Stable", label: "Stable" },
                        { value: "Terminal", label: "Terminal" },
                      ]}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minHeight: "45px",
                          height: "45px",
                        }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          padding: "4px",
                        }),
                        indicatorSeparator: () => ({ display: "none" }),
                      }}
                      isClearable
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderGroup = (group) => (
    <div key={group.group_id} className="col-xl-12 mb-4">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title mb-0">{group.group_name}</h4>
        </div>
        <div className="card-body">
          <div className="row">{group.questions.map((question) => renderQuestion(question))}</div>
        </div>
      </div>
    </div>
  )

  const formatDate = (dateString) => {
    if (!dateString || dateString === "0000-00-00") return "N/A"
    const d = new Date(dateString)
    const day = String(d.getDate()).padStart(2, "0")
    const month = String(d.getMonth() + 1).padStart(2, "0")
    const year = d.getFullYear()
    return `${day}-${month}-${year}`
  }

  // SHOW ALL standalone questions (including important and negative ones)
  const standaloneQuestions = answers ? answers.filter((item) => item.isGroup === "No") : []
  const groupedQuestions = answers ? answers.filter((item) => item.isGroup === "Yes") : []

  const showEmptyState =
    (!loading && !answers && !importantQuestions.length) ||
    (standaloneQuestions.length === 0 && groupedQuestions.length === 0 && combinedImportantAndNegativeQuestions.length === 0)

  if (loading) {
    return <div>Loading...</div>
  }

  if (error) {
    return <div>Error: {error}</div>
  }

  return (
    <>
      <div className="row">
        <div className="col-xl-12">
          <div className="d-flex justify-content-end mb-3">
            <Button variant="primary" onClick={() => navigate(-1)}>
              Back
            </Button>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-3 text-center">
                  <img
                    src={getValidPhotoUrl(patientDetails?.patient_photo) || "/placeholder.svg"}
                    alt="Patient Profile"
                    className="img-fluid rounded mb-2"
                    style={{ width: "150px", height: "150px", objectFit: "cover" }}
                    onError={(e) => {
                      e.target.onerror = null
                      e.target.src = defaultAvatar
                    }}
                  />
                  <div className="fw-bold">{patientDetails?.patient_code || "N/A"}</div>
                </div>

                <div className="col-md-3">
                  <div className="mb-2">
                    <span className="fw-bold d-block">Patient Name:</span>
                    <span>
                      {patientDetails
                        ? formatName(`${patientDetails.first_name || ""} ${patientDetails.last_name || ""}`.trim())
                        : "N/A"}
                    </span>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold d-block">Date of Birth:</span>
                    <span>{formatDate(patientDetails?.date_of_birth)}</span>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold d-block">Gender:</span>
                    <span>{patientDetails?.gender || "N/A"}</span>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold d-block">Occupation:</span>
                    <span>{patientDetails?.occupation || "N/A"}</span>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-2">
                    <span className="fw-bold d-block">Weight:</span>
                    <span>{Number(patientDetails?.weight) > 0 ? `${patientDetails.weight} kg` : "-"}</span>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold d-block">Height:</span>
                    <span>{Number(patientDetails?.height) > 0 ? `${patientDetails.height} cm` : "-"}</span>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold d-block">Current Disease:</span>
                    <span>{patientDetails?.diseases?.[0]?.disease_name || "N/A"}</span>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold d-block">Patient Status:</span>
                    <span>{patientDetails?.patient_status || "N/A"}</span>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-2">
                    <span className="fw-bold d-block">Contact No:</span>
                    {patientDetails?.whatsapp_contact_no || "N/A"}
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold d-block">Email:</span>
                    <span>{patientDetails?.patient_email || "N/A"}</span>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold d-block">ID Proof:</span>
                    <span>{patientDetails?.id_proof || "N/A"}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {showEmptyState ? (
            <div className="text-center py-4">
              <h4 className="mb-3">No Questionnaire Records Found</h4>
              <p className="text-muted">There are currently no questionnaire records available for this patient.</p>
            </div>
          ) : (
            <>
              {/* Combined Important & Negative Questions Section */}
              {combinedImportantAndNegativeQuestions.length > 0 && (
                <div className="mb-4">
                  <div className="d-flex align-items-center mb-3">
                    <h4 className="card-title mb-0">Important & Negative Questions</h4>
                  </div>
                  <div className="row">
                    {combinedImportantAndNegativeQuestions.map(renderQuestionWithCheckboxes)}
                  </div>
                </div>
              )}

              {/* General Questions Section - Shows ALL standalone questions */}
              {standaloneQuestions.length > 0 && (
                <div className="mb-4">
                  <div className="d-flex align-items-center mb-3">
                    <h4 className="card-title mb-0">General Questions</h4>
                  </div>
                  <div className="row">
                    {standaloneQuestions.map(renderQuestion)}
                  </div>
                </div>
              )}

              {groupedQuestions.length > 0 && (
                <div className="mb-4">
                  <div className="d-flex align-items-center mb-3">
                    <h4 className="card-title mb-0">Question Groups</h4>
                  </div>
                  <div className="row">{groupedQuestions.map(renderGroup)}</div>
                </div>
              )}

              <div className="d-flex justify-content-end">
                <button onClick={handleSave} className="btn btn-primary" disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default Questionnaire